import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  IGeoJSONCircle,
  IPopulate,
  IQueryParam,
  IUbicacionBase,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../../auxiliares/tabla/filtro/filtro.component';
import { DialogService } from '../../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { UbicacionService } from '../../../../../../auxiliares/servicios/http/ubicacion.service';
import { ClienteJerarquiaService } from '../../../../../../auxiliares/servicios/cliente-jerarquia.service';
import {
  esc,
  filtroActivo,
  estadoActivo,
  htmlContacto,
  htmlDireccion,
  tooltipDireccion,
  htmlEntidad,
} from '../../presentacion-entidades';
import { CrearEditarCentroAtencionComponent } from '../crear-editar-centro-atencion/crear-editar-centro-atencion.component';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';

type ICentro = IUbicacionBase<'Centro de Atención'>;

// La ruta conserva el parámetro `:tipoEmergencia` (hay links externos a
// `centrosAtencion/medica`), pero sólo existen bases de operaciones médicas.
const RUTA = 'main/emergencias/centrosAtencion/medica';

@Component({
  selector: 'app-listado-centros-atencion',
  imports: [AuxiliaresModule],
  templateUrl: './listado-centros-atencion.component.html',
})
export class CentroAtencionComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private dialogService = inject(DialogService);
  private service = inject(UbicacionService);
  private router = inject(Router);
  private formularios = inject(FormularioDialogService);
  private clienteJerarquia = inject(ClienteJerarquiaService);

  public name = 'CentroAtencionComponent';
  public datos?: ICentro[];
  public totalCount = 0;
  public columnas?: IColumnas<ICentro>[];
  private datos$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso(
      'Emergencias Médicas - Crear centros médicos',
    ),
    tooltip: 'Crear base de operaciones',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public search: IRegExpSearch = {
    fields: [
      'identificacion',
      'direccion',
      'valores.telefono',
      'valores.email',
    ],
  };

  private cliente: IFiltroTabla = {
    filter: { field: 'idCliente' },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // El cliente elegido y toda su descendencia.
  private ancestro: IFiltroTabla = {
    filter: { field: 'idsAncestros', incluirPropio: true },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [filtroActivo('valores.activo')];

  private populate: IPopulate[] = [{ path: 'cliente' }];
  public query: IQueryParam = {
    filter: JSON.stringify({ 'valores.tipoEmergencia': 'Médica' }),
    populate: JSON.stringify(this.populate),
    sort: 'identificacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public listar(): void {
    // El filtro de la tabla pisa `query.filter`: el tipo se agrega siempre.
    const filtro = this.query.filter ? JSON.parse(this.query.filter) : {};
    const query: IQueryParam = {
      ...this.query,
      filter: JSON.stringify({
        $and: [filtro, { 'valores.tipoEmergencia': 'Médica' }],
      }),
    };
    this.datos$?.unsubscribe();
    this.datos$ = this.lista2.centroAtencions(query, 15).subscribe((data) => {
      const datos = data.datos as ICentro[];
      if (HelperService.cambiaronLosDatos(this.datos, datos)) {
        this.datos = datos;
      }
      this.totalCount = data.totalCount;
    });
  }

  // Acciones

  public crear(): void {
    this.abrirFormulario();
  }

  public editar(centro: ICentro): void {
    this.abrirFormulario({ id: centro._id });
  }

  /** Crear/editar en diálogo; al guardar se refresca el listado. */
  private abrirFormulario(data?: { id?: string }): void {
    const ref = this.formularios.abrir(CrearEditarCentroAtencionComponent, {
      tamano: 'mediano',
      data,
    });
    ref.afterClosed().subscribe((guardado) => {
      if (guardado) this.listar();
    });
  }

  public detalles(centro: ICentro): void {
    this.router.navigate([RUTA, 'detalles', centro._id]);
  }

  public async eliminar(centro: ICentro): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Eliminar base de operaciones',
      `¿Confirmás eliminar la base de operaciones <strong>${esc(centro.identificacion)}</strong>?`,
    );
    if (!ok) return;
    try {
      await this.service.delete(centro._id!);
      this.helper.notifSuccess('Base de operaciones eliminada');
      this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas(): void {
    const columnas: IColumnas<ICentro>[] = [
      {
        header: {
          label: 'Base de operaciones',
          sort: 'identificacion',
          sortable: true,
        },
        row: {
          html: (d) => {
            const radio = (d.geojson as IGeoJSONCircle)?.radius;
            return htmlEntidad('apartment', d.identificacion, [
              radio
                ? `Cobertura de ${Math.round(radio).toLocaleString('es-AR')} m`
                : null,
            ]);
          },
        },
      },
      {
        header: { label: 'Estado', sort: 'valores.activo', sortable: true },
        row: { estado: (d) => estadoActivo(d.valores?.activo) },
      },
      {
        header: { label: 'Dirección', sort: 'direccion', sortable: true },
        row: {
          html: (d) => htmlDireccion(d.direccion),
          tooltip: (d) => tooltipDireccion(d.direccion),
        },
      },
      {
        header: { label: 'Contacto' },
        row: {
          html: (d) => htmlContacto(d.valores?.telefono, d.valores?.email),
        },
      },
      {
        header: {
          label: 'Fecha de alta',
          sort: 'fechaCreacion',
          sortable: true,
          hidden: true,
        },
        row: { fecha: (d) => d.fechaCreacion, noData: 'Sin fecha' },
      },
    ];

    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: { label: 'Cliente', hidden: true },
        row: {
          html: (d) => this.clienteJerarquia.htmlNombre(d.cliente),
          tooltip: (d) => this.clienteJerarquia.tooltipJerarquia(d.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    columnas.push({
      header: { label: 'Acciones' },
      row: {
        // Menú: Ver detalle primero, Eliminar al final
        accionesGrouped: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (d) => this.detalles(d),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (d) => this.editar(d),
            disabled: () =>
              !this.helper.tienePermiso(
                'Emergencias Médicas - Editar centros médicos',
              ),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (d) => this.eliminar(d),
            disabled: () =>
              !this.helper.tienePermiso(
                'Emergencias Médicas - Eliminar centros médicos',
              ),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  ngOnInit(): void {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.unshift(this.cliente, this.ancestro);
    }
    this.setColumnas();
    this.listar();
  }

  ngOnDestroy(): void {
    this.datos$?.unsubscribe();
  }
}
